// Dummy AI insights for the Insights issues report.
// Insights are derived from the report data with simple rules and returned after a simulated
// delay, so the UI can be built against the final shape before a real LLM endpoint exists.
// Swap getInsightsAiSummary() for an API call when the backend is in place.

import { formatNumber } from '@shared/utils/formatNumber';
import type { InsightsIssuesReportData } from './insights-dummy-data';
import { formatPercent } from './insights-theme';

export type InsightsAiInsightKind = 'positive' | 'negative' | 'recommendation';

export interface InsightsAiInsight {
    Kind: InsightsAiInsightKind;
    Title: string;
    Text: string;
}

export interface InsightsAiSummary {
    Summary: string;
    Insights: InsightsAiInsight[];
    GeneratedAt: string;
}

const SIMULATED_DELAY_MS = 3000;

// Reason values from the unsubscribe survey
const REASON_TOO_FREQUENT = 2;
const REASON_NEVER_SIGNED_UP = 3;

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function signedPp(value: number): string {
    return `${value >= 0 ? '+' : ''}${formatNumber(value, 1)} pp`;
}

function buildInsights(report: InsightsIssuesReportData): InsightsAiInsight[] {
    const insights: InsightsAiInsight[] = [];
    const trend = report.MonthlyTrend;
    const current = trend[trend.length - 1];
    const previous = trend[trend.length - 2];

    if (current && previous) {
        const openDelta = current.OpenPercentage - previous.OpenPercentage;
        if (Math.abs(openDelta) >= 0.5) {
            insights.push({
                Kind: openDelta > 0 ? 'positive' : 'negative',
                Title: openDelta > 0 ? 'Open rate is up' : 'Open rate is down',
                Text: `Open rate moved ${signedPp(openDelta)} to ${formatPercent(current.OpenPercentage)} compared to last month.`,
            });
        }

        const clickDelta = current.ClickPercentage - previous.ClickPercentage;
        if (Math.abs(clickDelta) >= 0.3) {
            insights.push({
                Kind: clickDelta > 0 ? 'positive' : 'negative',
                Title: clickDelta > 0 ? 'More recipients clicked' : 'Fewer recipients clicked',
                Text: `Click rate changed ${signedPp(clickDelta)} to ${formatPercent(current.ClickPercentage)}.`,
            });
        }

        const unsubscribeDelta = current.UnsubscribePercentage - previous.UnsubscribePercentage;
        if (unsubscribeDelta > 0.05) {
            insights.push({
                Kind: 'negative',
                Title: 'Unsubscribes increased',
                Text: `Unsubscribe rate rose to ${formatPercent(current.UnsubscribePercentage, 2)} (${signedPp(unsubscribeDelta)}).`,
            });
        }
    }

    const best = [...report.RecentIssues].sort((a, b) => b.Counts.OpenPercentage - a.Counts.OpenPercentage)[0];
    if (best && best.Counts.OpenPercentage > report.Totals.OpenPercentage) {
        insights.push({
            Kind: 'positive',
            Title: 'Best performing mailing',
            Text: `"${best.Subject}" reached a ${formatPercent(best.Counts.OpenPercentage)} open rate, ${signedPp(best.Counts.OpenPercentage - report.Totals.OpenPercentage)} above the average. Consider reusing its subject line style.`,
        });
    }

    const reasons = report.UnsubscribeReasons;
    const reasonTotal = reasons.reduce((acc, r) => acc + r.Count, 0);
    const topReason = [...reasons].sort((a, b) => b.Count - a.Count)[0];
    if (topReason && reasonTotal > 0) {
        const share = formatPercent((topReason.Count / reasonTotal) * 100, 0);
        if (topReason.ReasonValue === REASON_TOO_FREQUENT) {
            insights.push({
                Kind: 'recommendation',
                Title: 'Review send frequency',
                Text: `${share} of unsubscribers said they get too many emails. Try segmenting less engaged contacts into a lower frequency.`,
            });
        } else if (topReason.ReasonValue === REASON_NEVER_SIGNED_UP) {
            insights.push({
                Kind: 'recommendation',
                Title: 'Check your signup sources',
                Text: `${share} of unsubscribers say they never signed up. Consider double opt-in for new contacts.`,
            });
        }
    }

    if (report.MailClients.MobilePercentage >= 55) {
        insights.push({
            Kind: 'recommendation',
            Title: 'Design for mobile first',
            Text: `${formatPercent(report.MailClients.MobilePercentage, 0)} of opens happen on mobile. Keep subject lines short and buttons easy to tap.`,
        });
    }

    if (current && current.InactivePercentage >= 20) {
        insights.push({
            Kind: 'recommendation',
            Title: 'Re-engage inactive contacts',
            Text: `${formatPercent(current.InactivePercentage)} of recipients are inactive. A re-engagement journey can win some back and protect deliverability.`,
        });
    }

    return insights;
}

function buildSummary(report: InsightsIssuesReportData, monthLabel: string): string {
    const current = report.MonthlyTrend[report.MonthlyTrend.length - 1];
    if (!current) return `No mailings were sent in ${monthLabel}.`;
    return (
        `In ${monthLabel} you sent ${formatNumber(current.IssueCount)} mailings to ${formatNumber(current.SentCount)} recipients. ` +
        `${formatPercent(current.OpenPercentage)} opened and ${formatPercent(current.ClickPercentage)} clicked, ` +
        `with a delivery rate of ${formatPercent(current.ReceivedPercentage)}.`
    );
}

export async function getInsightsAiSummary(report: InsightsIssuesReportData, monthLabel: string): Promise<InsightsAiSummary> {
    await delay(SIMULATED_DELAY_MS);
    return {
        Summary: buildSummary(report, monthLabel),
        Insights: buildInsights(report).slice(0, 6),
        GeneratedAt: new Date().toISOString(),
    };
}
